import React, { useState, useEffect, useRef } from "react";
import useAxios from "../../hooks/useAxios";
import { useAuth } from "../../contexts/AuthContext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { Panel } from "primereact/panel";

const DATA_TYPES = [
  { label: "Voice & Video", value: "voice_video" },
  { label: "PS384", value: "ps384" },
  { label: "HSDPA", value: "hsdpa" },
];

const COLUMNS_BY_TYPE = {
  voice_video: ["CSTraff", "CSAt", "CSSR", "CSDR", "VPTr", "VPAt", "VPSR", "VPDR", "RRCAt", "RRCSR", "SHAt", "SHSR", "HHAt", "HHSR", "IRCSHAt", "IRCSHSR"],
  ps384: ["PSDL", "PSUL", "PSAt", "PSSR", "PSCDR", "IRPSHAt", "IRPSHSR"],
  hsdpa: ["HSTr", "HSAt", "HSSR", "HSCDR", "HSUsNum", "HSMeanTr"],
};

const KPI_RULES = {
  CSSR: [{ min: 0, max: 95, cls: "kpi-bad" }, { min: 95, max: 98, cls: "kpi-warning" }],
  CSDR: [{ min: 2, max: Infinity, cls: "kpi-bad" }, { min: 1, max: 2, cls: "kpi-warning" }],
  VPSR: [{ min: 0, max: 95, cls: "kpi-bad" }, { min: 95, max: 98, cls: "kpi-warning" }],
  VPDR: [{ min: 2, max: Infinity, cls: "kpi-bad" }, { min: 1, max: 2, cls: "kpi-warning" }],
  RRCSR: [{ min: 0, max: 95, cls: "kpi-bad" }, { min: 95, max: 98, cls: "kpi-warning" }],
  SHSR: [{ min: 0, max: 98, cls: "kpi-bad" }, { min: 98, max: 99, cls: "kpi-warning" }],
  HHSR: [{ min: 0, max: 85, cls: "kpi-bad" }, { min: 85, max: 90, cls: "kpi-warning" }],
  IRCSHSR: [{ min: 0, max: 85, cls: "kpi-bad" }, { min: 85, max: 90, cls: "kpi-warning" }],
  PSSR: [{ min: 0, max: 95, cls: "kpi-bad" }, { min: 95, max: 98, cls: "kpi-warning" }],
  PSCDR: [{ min: 2, max: Infinity, cls: "kpi-bad" }, { min: 1, max: 2, cls: "kpi-warning" }],
  IRPSHSR: [{ min: 0, max: 85, cls: "kpi-bad" }, { min: 85, max: 90, cls: "kpi-warning" }],
  HSSR: [{ min: 0, max: 95, cls: "kpi-bad" }, { min: 95, max: 98, cls: "kpi-warning" }],
  HSCDR: [{ min: 2, max: Infinity, cls: "kpi-bad" }, { min: 1, max: 2, cls: "kpi-warning" }],
};

const getKpiClass = (field, value) => {
  if (value === null || value === undefined) return "";
  const rules = KPI_RULES[field];
  if (!rules) return "";
  const numVal = parseFloat(value);
  if (isNaN(numVal)) return "";
  for (const rule of rules) {
    if (numVal >= rule.min && numVal < rule.max) return rule.cls;
  }
  return "";
};

const Stats3G = () => {
  const { user } = useAuth();
  const { response: rncResponse, fetchData: fetchRncData } = useAxios();
  const { response: ucellResponse, loading: loadingCells, fetchData: fetchUcellData } = useAxios();
  const { response: statsResponse, loading, fetchData: fetchStatsData } = useAxios();

  const [rncList, setRncList] = useState([]);
  const [ucellList, setUcellList] = useState([]);
  const [selectedRnc, setSelectedRnc] = useState(null);
  const [selectedUcells, setSelectedUcells] = useState([]);
  const [date1, setDate1] = useState(new Date());
  const [date2, setDate2] = useState(new Date());
  const [reportType, setReportType] = useState("hourly");
  const [dataType, setDataType] = useState("voice_video");
  const [tableData, setTableData] = useState([]);

  const toast = useRef(null);
  const dt = useRef(null);

  useEffect(() => {
    fetchRncData({ method: "get", url: "/api/stats-3g/rnc-list" });
  }, []);

  useEffect(() => {
    if (!rncResponse) return;
    if (rncResponse.status === "success" && rncResponse.data) {
      setRncList(rncResponse.data.map((r) => ({ label: r.name, value: r.id })));
    }
  }, [rncResponse]);

  useEffect(() => {
    if (!ucellResponse) return;
    if (ucellResponse.status === "success" && ucellResponse.data) {
      setUcellList(ucellResponse.data.map((c) => ({ label: c.name, value: c.id })));
    }
  }, [ucellResponse]);

  useEffect(() => {
    if (!statsResponse) return;
    if (statsResponse.status === "success" && statsResponse.data) {
      setTableData(statsResponse.data);
    } else if (statsResponse.status === "error") {
      toast.current?.show({ severity: "error", detail: statsResponse.message, life: 3000 });
    }
  }, [statsResponse]);

  const handleRncChange = (e) => {
    setSelectedRnc(e.value);
    setSelectedUcells([]);
    setUcellList([]);
    if (e.value) {
      const rncId = e.value === "all" ? "rnc_all" : e.value;
      fetchUcellData({ method: "get", url: "/api/stats-3g/ucells", params: { rncId } });
    }
  };

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSearch = () => {
    if (selectedUcells.length === 0) {
      toast.current.show({ severity: "warn", detail: "Выберите сектора", life: 3000 });
      return;
    }
    fetchStatsData({
      method: "post",
      url: "/api/stats-3g",
      data: {
        ucellIds: selectedUcells,
        date1: formatDate(date1),
        date2: formatDate(date2),
        reportType,
        dataType,
      },
    });
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const kpiBodyTemplate = (field) => (rowData) => {
    const value = rowData[field];
    if (value === null || value === undefined) return "n/a";
    const cls = getKpiClass(field, value);
    const display = typeof value === "number" ? Math.round(value * 10) / 10 : value;
    return <span className={cls}>{display}</span>;
  };

  if (!user) return null;

  const columns = COLUMNS_BY_TYPE[dataType] || [];

  return (
    <div className="flex justify-content-center">
      <div style={{ width: "100%", maxWidth: "1600px", padding: "1rem" }}>
        <Toast ref={toast} position="top-right" />

        <Panel header="📶 Параметры отчета 3G" className="mb-4">
          <div className="grid">
            <div className="col-12 md:col-2">
              <label className="block mb-1 font-bold text-sm">Контроллер:</label>
              <Dropdown
                value={selectedRnc}
                options={[{ label: "Все", value: "all" }, ...rncList]}
                onChange={handleRncChange}
                placeholder="Выберите RNC"
                className="w-full p-inputtext-sm"
              />
            </div>
            <div className="col-12 md:col-2">
              <label className="block mb-1 font-bold text-sm">Сектора:</label>
              <MultiSelect
                value={selectedUcells}
                options={ucellList}
                onChange={(e) => setSelectedUcells(e.value)}
                placeholder="Выберите сектора"
                className="w-full p-inputtext-sm"
                maxSelectedLabels={3}
                filter
                loading={loadingCells}
              />
            </div>
            <div className="col-6 md:col-2">
              <label className="block mb-1 font-bold text-sm">Тип данных:</label>
              <Dropdown value={dataType} options={DATA_TYPES} onChange={(e) => setDataType(e.value)} className="w-full p-inputtext-sm" />
            </div>
            <div className="col-6 md:col-2">
              <label className="block mb-1 font-bold text-sm">Тип отчета:</label>
              <Dropdown
                value={reportType}
                options={[
                  { label: "За каждый час", value: "hourly" },
                  { label: "В ЧНН", value: "chnn" },
                ]}
                onChange={(e) => setReportType(e.value)}
                className="w-full p-inputtext-sm"
              />
            </div>
            <div className="col-6 md:col-1">
              <label className="block mb-1 font-bold text-sm">С:</label>
              <Calendar value={date1} onChange={(e) => setDate1(e.value)} dateFormat="yy-mm-dd" className="w-full p-inputtext-sm" showIcon />
            </div>
            <div className="col-6 md:col-1">
              <label className="block mb-1 font-bold text-sm">По:</label>
              <Calendar value={date2} onChange={(e) => setDate2(e.value)} dateFormat="yy-mm-dd" className="w-full p-inputtext-sm" showIcon />
            </div>
            <div className="col-12 md:col-2 flex align-items-end gap-2">
              <Button label="Показать" icon="pi pi-eye" className="p-button-primary p-button-sm" onClick={handleSearch} />
              <Button label="CSV" icon="pi pi-file" className="p-button-success p-button-sm" onClick={exportCSV} disabled={tableData.length === 0} />
            </div>
          </div>
        </Panel>

        {loading ? (
          <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
        ) : tableData.length > 0 ? (
          <DataTable
            ref={dt}
            value={tableData}
            paginator
            rows={100}
            rowsPerPageOptions={[50, 100, 500]}
            size="small"
            showGridlines
            stripedRows
            scrollable
            scrollHeight="600px"
            exportFilename="3g_stats"
            style={{ fontSize: "0.8rem" }}
            emptyMessage="Нет данных"
          >
            <Column field="name" header="Ucell" sortable frozen style={{ minWidth: "120px" }} />
            <Column field="formatted_date" header="Date" sortable style={{ minWidth: "130px" }} />
            {columns.map((col) => (
              <Column key={col} field={col} header={col} sortable body={kpiBodyTemplate(col)} style={{ minWidth: "80px" }} />
            ))}
          </DataTable>
        ) : null}
      </div>

      <style>{`
        .kpi-bad { background-color: #ffcdd2; padding: 2px 6px; border-radius: 3px; }
        .kpi-warning { background-color: #fff9c4; padding: 2px 6px; border-radius: 3px; }
      `}</style>
    </div>
  );
};

export default Stats3G;
